import React, { useState, useEffect, useMemo } from "react";
import "./css/AbsenceForShiftPopup.css";
import { useAuth } from "../navigation/AuthProvider";

const toMin = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return NaN;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const AbsenceForShiftPopup = ({
  employee,
  absenceRules,
  onAdd,
  onClose,
  initialAbsence,
  editIndex,
  employeeDayShifts = [],
  currentShiftId,
}) => {

  const { projects} = useAuth();
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [from, setFrom] = useState("08:00");
  const [to, setTo] = useState("17:00");
  const [wholeShift, setWholeShift] = useState(true);
  const [pendingAbsence, setPendingAbsence] = useState(null);
  const [showApplyDialog, setShowApplyDialog] = useState(false);

  useEffect(() => {
    if (initialAbsence) {
      setSelectedRuleId(initialAbsence.absenceType?.toString() || "");
      setFrom(initialAbsence.from || "08:00");
      setTo(initialAbsence.to || "17:00");
      setWholeShift(!!initialAbsence.appliesWholeShift);
    }
  }, [initialAbsence]);

  const selectedRule = useMemo(() => {
    return absenceRules?.find((r) => r._id?.toString() === selectedRuleId);
  }, [absenceRules, selectedRuleId]);

  const otherShifts = useMemo(() => {
    return (employeeDayShifts || []).filter((shift) => shift._id !== currentShiftId);
  }, [employeeDayShifts, currentShiftId]);

  const timeError = useMemo(() => {
    if (wholeShift) return "";
    if (!from || !to) return "Fyll i både från- och tilltid.";
    const f = toMin(from);
    const t = toMin(to);
    if (isNaN(f) || isNaN(t)) return "Ogiltigt tidsformat.";
    return "";
  }, [from, to, wholeShift]);

  const saveAbsence = (applyToAllDayShifts = false) => {
    const absence = {
      absenceType: selectedRuleId.toString(),
      appliesWholeShift: wholeShift,
      from: wholeShift ? "" : from,
      to: wholeShift ? "" : to,
      applyToAllDayShifts,
    };
    if (typeof editIndex === "number") {
      onAdd(absence, editIndex);
    } else {
      onAdd(absence);
    }
    onClose();
  };

  const handleAddAbsence = (e) => {
    e?.preventDefault?.();
    if (!selectedRuleId) {
      alert("Välj en frånvarotyp.");
      return;
    }
    if (!wholeShift) {
      if (!from || !to) {
        alert("Fyll i både från- och tilltid.");
        return;
      }
      const f = toMin(from);
      const t = toMin(to);
      if (isNaN(f) || isNaN(t)) {
        alert("Ogiltigt tidsformat.");
        return;
      }
      const dur = (t - f + 1440) % 1440;
      if (dur <= 0) {
        alert("Tidsintervallet är ogiltigt.");
        return;
      }
    }
    const absence = {
      absenceType: selectedRuleId.toString(),
      appliesWholeShift: wholeShift,
      from: wholeShift ? "" : from,
      to: wholeShift ? "" : to,
    };
    if (otherShifts.length > 0) {
      setPendingAbsence(absence);
      setShowApplyDialog(true);
      return;
    }
    saveAbsence(false);
  };

  const handleApplyChoice = (applyToAll) => {
    const absence = { ...pendingAbsence, applyToAllDayShifts: applyToAll };
    if (typeof editIndex === "number") {
      onAdd(absence, editIndex);
    } else {
      onAdd(absence);
    }
    onClose();
  };

  return (
    <>
      <div className="absence-modal-overlay" onClick={onClose}>
        <div className="absence-modal" onClick={(e) => e.stopPropagation()}>
          {/* HEADER – utan ikon */}
          <div className="modal-header">
            <div className="header-text">
              <h2>{editIndex !== null ? "Redigera frånvaro" : "Registrera frånvaro"}</h2>
              <p>{employee?.name || "Okänd anställd"}</p>
            </div>
          </div>

          {/* BODY */}
          <div className="modal-body">
            {/* Frånvarotyp */}
            <div className="field-group">
              <label>Frånvarotyp</label>
              <div className="select-wrapper">
                <select
                  value={selectedRuleId}
                  onChange={(e) => setSelectedRuleId(e.target.value)}
                >
                  <option value="">Välj typ av frånvaro</option>
                  {absenceRules?.map((rule) => (
                    <option key={rule._id} value={rule._id.toString()}>
                      {rule.name} ({rule.percentage}% lön)
                    </option>
                  ))}
                </select>
              </div>
              {/* Ingen rule-hint här */}
            </div>

            {/* Hela passet toggle */}
            <div className="field-group">
              <div className="toggle-switch">
                <label className="switch-label">
                  <input
                    type="checkbox"
                    checked={wholeShift}
                    onChange={() => setWholeShift(!wholeShift)}
                  />
                  <span className="switch-slider"></span>
                  <span className="switch-text">Gäller hela passet</span>
                </label>
              </div>
            </div>

            {/* Tidsintervall – utan gul bakgrund */}
            {!wholeShift && (
              <div className="field-group">
                <label>Tidsintervall för frånvaro</label>
                <div className="time-inputs">
                  <div className="time-input-wrapper">
                    <input
                      type="time"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>
                  <span className="time-separator">–</span>
                  <div className="time-input-wrapper">
                    <input
                      type="time"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </div>
                {timeError && <div className="error-message">{timeError}</div>}
              </div>
            )}
          </div>

          {/* ACTIONS */}
          <div className="modal-actions">
            <button className="btn-secondary" onClick={onClose}>
              Avbryt
            </button>
            <button
              className="btn-primary"
              onClick={handleAddAbsence}
              disabled={!selectedRuleId || (!!timeError && !wholeShift)}
            >
              {editIndex !== null ? "Uppdatera" : "Lägg till"}
            </button>
          </div>
        </div>
      </div>

      {/* BEKRÄFTELSE-DIALOG – utan ikon */}
      {showApplyDialog && (
        <div className="absence-modal-overlay" onClick={() => setShowApplyDialog(false)}>
          <div className="absence-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <h3>Applicera på fler pass?</h3>
            </div>
            <div className="confirm-body">
              <p>
                <strong>{employee?.name}</strong> har ytterligare pass samma dag.
                <br />
                Vill du applicera samma frånvaro på alla dessa pass?
              </p>
              <div className="other-shifts-list">
                {otherShifts.map((shift) => (
                  <div key={shift._id} className="other-shift-item">
                    <div className="shift-project">{projects.find((project) => project._id === shift.project)?.name || "Okänt projekt"}</div>
                    <div className="shift-time">{shift.startTime} – {shift.endTime}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => handleApplyChoice(false)}>
                Nej, bara detta pass
              </button>
              <button className="btn-primary" onClick={() => handleApplyChoice(true)}>
                Ja, applicera på alla
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AbsenceForShiftPopup;